import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowLeft, ArrowRight, Check, Loader2, Plus, X } from "lucide-react";
import { Button, Input, Label, cn } from "@eduviaos/ui-kit";
import { Ziggy } from "../../components/Ziggy";
import type { ZiggyAge } from "../../lib/ziggy";
import { useCreatePathway, useGenerateRoadmap, useUpdatePathway } from "../../lib/learning/hooks";
import {
  PURPOSE_OPTIONS,
  purposeWantsExam,
  purposeWantsPace,
  purposeWantsRoadmap,
} from "../../lib/learning/copy";
import {
  localizedBookLabel,
  localizedDefaultTitle,
  localizedRoadmapLabel,
  localizedSubjectsCopy,
} from "../../lib/i18n/intakeWizard";
import type { BookSource, LearningPurpose, Pathway, RoadmapSource } from "../../lib/learning/types";
import type { PortalStrings } from "../../lib/i18n/usePortalLocale";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";
import { localizedPurposeCopy } from "../../lib/i18n/intakeWizard";
import { apiErrorMessage } from "../../lib/apiErrorCopy";
import type { PortalLocale } from "../../lib/i18n/portalShell";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

type StepKey = "purpose" | "details" | "roadmap" | "book" | "pace" | "review";

interface Draft {
  purpose: LearningPurpose | null;
  title: string;
  titleTouched: boolean;
  examName: string;
  subjects: string[];
  roadmapSource: RoadmapSource | null;
  bookSource: BookSource | null;
  bookTitle: string;
  daysBudget: string;
  dailyMinutes: string;
}

const EMPTY: Draft = {
  purpose: null,
  title: "",
  titleTouched: false,
  examName: "",
  subjects: [],
  roadmapSource: null,
  bookSource: null,
  bookTitle: "",
  daysBudget: "",
  dailyMinutes: "",
};

function draftFromPathway(p: Pathway): Draft {
  return {
    purpose: p.purpose,
    title: p.title,
    titleTouched: true,
    examName: p.exam_name ?? "",
    subjects: [...p.subjects],
    roadmapSource: p.roadmap_source,
    bookSource: p.book_source,
    bookTitle: p.book_title ?? "",
    daysBudget: p.days_budget != null ? String(p.days_budget) : "",
    dailyMinutes: p.daily_minutes != null ? String(p.daily_minutes) : "",
  };
}

/** Resume incomplete plans at the first step that still needs data. */
function resumeStepIndex(p: Pathway): number {
  const steps = stepsFor(p.purpose);
  if (!p.subjects.some((s) => s.trim().length > 0)) return Math.max(0, steps.indexOf("details"));
  if (purposeWantsRoadmap(p.purpose) && !p.roadmap_source)
    return Math.max(0, steps.indexOf("roadmap"));
  if (!p.book_source) return Math.max(0, steps.indexOf("book"));
  return Math.max(0, steps.indexOf("review"));
}

function trimmedSubjects(subjects: string[]): string[] {
  return subjects.map((s) => s.trim()).filter((s) => s.length > 0);
}

function intakeErrorMessage(err: unknown, locale: PortalLocale, s: PortalStrings): string {
  return apiErrorMessage(
    err,
    locale,
    {
      errorOffline: s.intakeErrorOffline,
      errorTooManyRequests: s.errorTooManyRequests,
      errorServiceDown: s.intakeError503,
    },
    s.intakeErrorSaveFailed,
  );
}

/** The ordered steps for a given purpose (casual reading skips roadmap + pace). */
function stepsFor(purpose: LearningPurpose | null): StepKey[] {
  const steps: StepKey[] = ["purpose", "details"];
  if (purpose && purposeWantsRoadmap(purpose)) steps.push("roadmap");
  steps.push("book");
  if (purpose && purposeWantsPace(purpose)) steps.push("pace");
  steps.push("review");
  return steps;
}

/**
 * Study-centre learning intake wizard (Phase SC-3b). Walks the learner
 * from purpose → details → roadmap → book → pace → review, then creates a
 * real `LearningPathway` via the study-centre-gated API and marks its
 * onboarding complete. De-schooled throughout — the copy adapts to the
 * chosen purpose (casual readers never see a roadmap or a pace budget).
 */
export function IntakeWizard({
  ziggyAge = "grown",
  resumePathway = null,
  onClose,
  onCreated,
}: {
  ziggyAge?: ZiggyAge;
  /** When set, finish an incomplete pathway instead of creating a new one. */
  resumePathway?: Pathway | null;
  onClose: () => void;
  onCreated: (pathway: Pathway) => void;
}) {
  const reduce = useReducedMotion();
  const { strings: s, locale } = usePortalLocale();
  const [draft, setDraft] = React.useState<Draft>(() =>
    resumePathway ? draftFromPathway(resumePathway) : EMPTY,
  );
  const [stepIndex, setStepIndex] = React.useState(() =>
    resumePathway ? resumeStepIndex(resumePathway) : 0,
  );
  const [error, setError] = React.useState<string | null>(null);

  const createMut = useCreatePathway();
  const updateMut = useUpdatePathway();
  const generateRoadmapMut = useGenerateRoadmap();
  // Roadmap generation is intentionally excluded — it runs in the background
  // after the wizard hands off, so it must not gate the finish button.
  const submitting = createMut.isPending || updateMut.isPending;

  const steps = stepsFor(draft.purpose);
  const step = steps[Math.min(stepIndex, steps.length - 1)]!;
  const progress = Math.round((stepIndex / (steps.length - 1)) * 100);

  const patch = React.useCallback((next: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...next }));
  }, []);

  const effectiveTitle = draft.titleTouched
    ? draft.title
    : localizedDefaultTitle(locale, draft.purpose ?? "other", {
        examName: draft.examName,
        subjects: draft.subjects,
      });

  function back() {
    setError(null);
    if (stepIndex === 0) {
      onClose();
      return;
    }
    setStepIndex((i) => i - 1);
  }

  function canAdvance(): boolean {
    if (step === "purpose") return draft.purpose !== null;
    if (step === "details") {
      return effectiveTitle.trim().length > 0 && trimmedSubjects(draft.subjects).length > 0;
    }
    if (step === "roadmap") return draft.roadmapSource !== null;
    if (step === "book") {
      if (draft.bookSource === "self_specified") return draft.bookTitle.trim().length > 0;
      return draft.bookSource !== null;
    }
    return true;
  }

  async function advance() {
    setError(null);
    if (step !== "review") {
      setStepIndex((i) => i + 1);
      return;
    }
    // Final step — create (or resume-update) the pathway, generate roadmap if needed,
    // then flip onboarding_complete.
    const purpose = draft.purpose ?? "other";
    const subjects = trimmedSubjects(draft.subjects);
    const body = {
      title: effectiveTitle.trim(),
      purpose,
      exam_name: purposeWantsExam(purpose) && draft.examName.trim() ? draft.examName.trim() : null,
      subjects,
      days_budget: purposeWantsPace(purpose) ? toIntOrNull(draft.daysBudget) : null,
      daily_minutes: purposeWantsPace(purpose) ? toIntOrNull(draft.dailyMinutes) : null,
      roadmap_source: purposeWantsRoadmap(purpose) ? draft.roadmapSource : null,
      book_source: draft.bookSource,
      book_title:
        draft.bookSource === "self_specified" && draft.bookTitle.trim()
          ? draft.bookTitle.trim()
          : null,
    };
    try {
      const pathwayId = resumePathway?.id;
      const saved = pathwayId
        ? await updateMut.mutateAsync({ id: pathwayId, body })
        : await createMut.mutateAsync(body);
      // Flip onboarding complete first so a slow/failed AI roadmap does not
      // trap the learner in the finish-setup loop (SC-20).
      const done = await updateMut.mutateAsync({
        id: saved.id,
        body: { onboarding_complete: true },
      });
      if (draft.roadmapSource === "ai_generated") {
        // Fire-and-forget: roadmap generation is a multi-second AI call and
        // awaiting it here left the learner staring at the review step until
        // it resolved. The mutation carries a shared key, so the plan page
        // picks up the in-flight state and renders progress there instead.
        generateRoadmapMut.mutate({ id: saved.id });
      }
      onCreated(done);
    } catch (err) {
      setError(intakeErrorMessage(err, locale, s));
    }
  }

  return (
    <div className="space-y-4">
      <StepHeader
        title={s.setupTitle}
        step={s.stepLabel(stepIndex + 1, steps.length)}
        onBack={back}
        disabled={submitting}
        backLabel={s.back}
      />

      <div className="bg-muted h-2 overflow-hidden rounded-full">
        <motion.div
          className="bg-primary h-full rounded-full"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: reduce ? 0 : 0.3, ease: EASE_OUT }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={reduce ? false : { opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, x: -16 }}
          transition={{ duration: 0.24, ease: EASE_OUT }}
          className="space-y-4"
        >
          {step === "purpose" && (
            <PurposeStep
              ziggyAge={ziggyAge}
              locale={locale}
              strings={s}
              value={draft.purpose}
              onPick={(purpose) => {
                patch({ purpose });
                // Auto-advance on pick keeps the flow snappy.
                setStepIndex((i) => i + 1);
              }}
            />
          )}

          {step === "details" && (
            <DetailsStep draft={draft} effectiveTitle={effectiveTitle} onPatch={patch} />
          )}

          {step === "roadmap" && (
            <ChoiceStep
              heading={s.wantRoadmapTitle}
              blurb={s.wantRoadmapBlurb}
              options={[
                { value: "ai_generated", label: localizedRoadmapLabel(locale).ai_generated },
                { value: "self_chosen", label: localizedRoadmapLabel(locale).self_chosen },
              ]}
              value={draft.roadmapSource}
              onPick={(v) => patch({ roadmapSource: v as RoadmapSource })}
            />
          )}

          {step === "book" && (
            <BookStep
              purpose={draft.purpose ?? "other"}
              bookSource={draft.bookSource}
              bookTitle={draft.bookTitle}
              onPatch={patch}
            />
          )}

          {step === "pace" && (
            <PaceStep
              daysBudget={draft.daysBudget}
              dailyMinutes={draft.dailyMinutes}
              onPatch={patch}
            />
          )}

          {step === "review" && (
            <ReviewStep ziggyAge={ziggyAge} draft={draft} effectiveTitle={effectiveTitle} />
          )}
        </motion.div>
      </AnimatePresence>

      {error && (
        <p
          role="alert"
          className="bg-destructive/10 text-destructive rounded-lg px-3 py-2 text-center text-sm font-medium"
        >
          {error}
        </p>
      )}

      {/* Purpose auto-advances, so its footer button is hidden. */}
      {step !== "purpose" && (
        <Button
          type="button"
          onClick={advance}
          disabled={!canAdvance() || submitting}
          className="h-12 w-full rounded-xl text-base font-semibold transition-transform duration-150 ease-out active:scale-[0.98]"
        >
          {submitting ? (
            <Loader2 className="size-5 animate-spin" />
          ) : step === "review" ? (
            <>
              {s.finish} <Check className="size-4" />
            </>
          ) : (
            <>
              {s.next} <ArrowRight className="size-4" />
            </>
          )}
        </Button>
      )}
    </div>
  );
}

function toIntOrNull(v: string): number | null {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/* ─────────────────────────────── steps ───────────────────────────────── */

function PurposeStep({
  ziggyAge,
  locale,
  strings: s,
  value,
  onPick,
}: {
  ziggyAge: ZiggyAge;
  locale: "en" | "hi";
  strings: PortalStrings;
  value: LearningPurpose | null;
  onPick: (p: LearningPurpose) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Ziggy pose="curious" age={ziggyAge} size={56} glow={false} float={false} alt="" />
        <div>
          <p className="font-display text-lg font-semibold leading-tight">{s.purposeTitle}</p>
          <p className="text-muted-foreground text-sm">{s.pickWhatFits}</p>
        </div>
      </div>
      <div className="grid gap-2.5">
        {PURPOSE_OPTIONS.map((opt) => {
          const selected = value === opt.value;
          const copy = localizedPurposeCopy(locale, opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onPick(opt.value)}
              className={cn(
                "flex items-center gap-3.5 rounded-2xl border p-3.5 text-left transition-all duration-150 ease-out hover:-translate-y-0.5 active:scale-[0.99]",
                selected
                  ? "border-primary bg-primary/8"
                  : "border-border/60 bg-card hover:border-primary/40",
              )}
            >
              <span className="bg-primary/12 text-primary grid size-11 shrink-0 place-items-center rounded-xl">
                <opt.icon className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{copy.label}</p>
                <p className="text-muted-foreground text-xs">{copy.description}</p>
              </div>
              <ArrowRight className="text-muted-foreground size-4 shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DetailsStep({
  draft,
  effectiveTitle,
  onPatch,
}: {
  draft: Draft;
  effectiveTitle: string;
  onPatch: (next: Partial<Draft>) => void;
}) {
  const { strings: s, locale: _locale } = usePortalLocale();
  const purpose = draft.purpose ?? "other";
  const subj = localizedSubjectsCopy(_locale, purpose);
  return (
    <div className="space-y-5">
      {purposeWantsExam(purpose) && (
        <div className="space-y-2">
          <Label htmlFor="exam">{s.whichExam}</Label>
          <Input
            id="exam"
            autoFocus
            placeholder={s.examPlaceholder}
            value={draft.examName}
            onChange={(e) => onPatch({ examName: e.target.value })}
            className="h-12 rounded-xl text-base"
          />
        </div>
      )}

      <ChipInput
        label={subj.label}
        placeholder={subj.placeholder}
        values={draft.subjects}
        onChange={(subjects) => onPatch({ subjects })}
      />

      <div className="space-y-2">
        <Label htmlFor="title">{s.nameYourPlan}</Label>
        <Input
          id="title"
          placeholder={s.planNamePlaceholder}
          value={effectiveTitle}
          onChange={(e) => onPatch({ title: e.target.value, titleTouched: true })}
          className="h-12 rounded-xl text-base"
        />
        <p className="text-muted-foreground text-xs">{s.titleAutoHint}</p>
      </div>
    </div>
  );
}

function ChoiceStep({
  heading,
  blurb,
  options,
  value,
  onPick,
}: {
  heading: string;
  blurb: string;
  options: { value: string; label: string }[];
  value: string | null;
  onPick: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <p className="font-display text-lg font-semibold leading-tight">{heading}</p>
        <p className="text-muted-foreground mt-1 text-sm">{blurb}</p>
      </div>
      <div className="grid gap-2.5">
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onPick(opt.value)}
              className={cn(
                "flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-all duration-150 ease-out active:scale-[0.99]",
                selected
                  ? "border-primary bg-primary/8"
                  : "border-border/60 bg-card hover:border-primary/40",
              )}
            >
              <span className="text-sm font-semibold">{opt.label}</span>
              <span
                className={cn(
                  "grid size-5 shrink-0 place-items-center rounded-full border",
                  selected ? "border-primary bg-primary text-primary-foreground" : "border-border",
                )}
              >
                {selected && <Check className="size-3.5" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BookStep({
  purpose,
  bookSource,
  bookTitle,
  onPatch,
}: {
  purpose: LearningPurpose;
  bookSource: BookSource | null;
  bookTitle: string;
  onPatch: (next: Partial<Draft>) => void;
}) {
  const { strings: s, locale: _locale } = usePortalLocale();
  const blurb = purpose === "casual_reading" ? s.bookStepCasualHint : s.bookStepDefaultHint;
  return (
    <div className="space-y-4">
      <div>
        <p className="font-display text-lg font-semibold leading-tight">{s.whichBooks}</p>
        <p className="text-muted-foreground mt-1 text-sm">{blurb}</p>
      </div>
      <div className="grid gap-2.5">
        {(["ai_pick", "self_specified"] as BookSource[]).map((src) => {
          const selected = bookSource === src;
          return (
            <button
              key={src}
              type="button"
              aria-pressed={selected}
              onClick={() => onPatch({ bookSource: src })}
              className={cn(
                "flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-all duration-150 ease-out active:scale-[0.99]",
                selected
                  ? "border-primary bg-primary/8"
                  : "border-border/60 bg-card hover:border-primary/40",
              )}
            >
              <span className="text-sm font-semibold">{localizedBookLabel(_locale)[src]}</span>
              <span
                className={cn(
                  "grid size-5 shrink-0 place-items-center rounded-full border",
                  selected ? "border-primary bg-primary text-primary-foreground" : "border-border",
                )}
              >
                {selected && <Check className="size-3.5" />}
              </span>
            </button>
          );
        })}
      </div>
      {bookSource === "self_specified" && (
        <div className="space-y-2">
          <Label htmlFor="book">{s.bookName}</Label>
          <Input
            id="book"
            autoFocus
            placeholder={s.bookTitlePlaceholder}
            value={bookTitle}
            onChange={(e) => onPatch({ bookTitle: e.target.value })}
            className="h-12 rounded-xl text-base"
          />
        </div>
      )}
    </div>
  );
}

function PaceStep({
  daysBudget,
  dailyMinutes,
  onPatch,
}: {
  daysBudget: string;
  dailyMinutes: string;
  onPatch: (next: Partial<Draft>) => void;
}) {
  const { strings: s } = usePortalLocale();
  return (
    <div className="space-y-5">
      <div>
        <p className="font-display text-lg font-semibold leading-tight">{s.paceTitle}</p>
        <p className="text-muted-foreground mt-1 text-sm">{s.paceBlurb}</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="days">{s.daysToGoal}</Label>
        <Input
          id="days"
          type="number"
          inputMode="numeric"
          min={1}
          max={3650}
          placeholder={s.daysPlaceholder}
          value={daysBudget}
          onChange={(e) => onPatch({ daysBudget: e.target.value })}
          className="h-12 rounded-xl text-base"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="minutes">{s.minutesPerDay}</Label>
        <Input
          id="minutes"
          type="number"
          inputMode="numeric"
          min={5}
          max={1440}
          placeholder={s.minutesPlaceholder}
          value={dailyMinutes}
          onChange={(e) => onPatch({ dailyMinutes: e.target.value })}
          className="h-12 rounded-xl text-base"
        />
      </div>
    </div>
  );
}

function ReviewStep({
  ziggyAge,
  draft,
  effectiveTitle,
}: {
  ziggyAge: ZiggyAge;
  draft: Draft;
  effectiveTitle: string;
}) {
  const { strings: s, locale: _locale } = usePortalLocale();
  const purpose = draft.purpose ?? "other";
  const purposeCopy = localizedPurposeCopy(_locale, purpose);
  const rows: { label: string; value: string }[] = [
    { label: s.reviewGoal, value: purposeCopy.label },
  ];
  if (purposeWantsExam(purpose) && draft.examName.trim())
    rows.push({ label: s.reviewExam, value: draft.examName.trim() });
  if (draft.subjects.length) rows.push({ label: s.reviewFocus, value: draft.subjects.join(", ") });
  if (purposeWantsRoadmap(purpose) && draft.roadmapSource)
    rows.push({
      label: s.reviewRoadmap,
      value: localizedRoadmapLabel(_locale)[draft.roadmapSource],
    });
  if (draft.bookSource)
    rows.push({ label: s.reviewBooks, value: localizedBookLabel(_locale)[draft.bookSource] });
  if (draft.bookSource === "self_specified" && draft.bookTitle.trim())
    rows.push({ label: s.reviewBook, value: draft.bookTitle.trim() });
  if (purposeWantsPace(purpose) && draft.daysBudget.trim())
    rows.push({ label: s.reviewTimeline, value: s.reviewDays(draft.daysBudget.trim()) });
  if (purposeWantsPace(purpose) && draft.dailyMinutes.trim())
    rows.push({ label: s.reviewDaily, value: s.reviewMinPerDay(draft.dailyMinutes.trim()) });

  return (
    <div className="space-y-4">
      <div className="hero-celebrate hero-orbs relative overflow-hidden rounded-[1.25rem] p-4 text-white">
        <div className="relative z-10 flex items-center gap-3">
          <Ziggy pose="cheerful" age={ziggyAge} size={64} glow={false} float alt="" />
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg font-semibold leading-tight">{effectiveTitle}</p>
            <p className="text-sm text-white/85">{s.reviewReadyHint}</p>
          </div>
        </div>
      </div>
      <div className="border-border/60 bg-card divide-border/60 divide-y rounded-[1.25rem] border">
        {rows.map((r) => (
          <div key={r.label} className="flex items-start justify-between gap-4 px-4 py-3">
            <span className="text-muted-foreground text-sm">{r.label}</span>
            <span className="text-right text-sm font-semibold">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────── shared ──────────────────────────────── */

function ChipInput({
  label,
  placeholder,
  values,
  onChange,
}: {
  label: string;
  placeholder: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const { strings: s } = usePortalLocale();
  const [draft, setDraft] = React.useState("");
  const inputId = React.useId();

  function add() {
    const v = draft.trim();
    if (!v || values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...values, v]);
    setDraft("");
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>{label}</Label>
      <div className="flex gap-2">
        <Input
          id={inputId}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          className="h-12 rounded-xl text-base"
        />
        <button
          type="button"
          onClick={add}
          aria-label={s.addChip(label)}
          className="bg-primary/12 text-primary grid size-12 shrink-0 place-items-center rounded-xl transition-transform duration-150 ease-out active:scale-95"
        >
          <Plus className="size-5" />
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {values.map((v) => (
            <span
              key={v}
              className="border-border/60 bg-muted/50 flex items-center gap-1.5 rounded-full border py-1 pl-3 pr-1.5 text-xs font-medium"
            >
              {v}
              <button
                type="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                aria-label={s.removeChip(v)}
                className="text-muted-foreground hover:text-foreground grid size-4 place-items-center rounded-full"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function StepHeader({
  title,
  step,
  onBack,
  disabled,
  backLabel = "Back",
}: {
  title: string;
  step: string;
  onBack: () => void;
  disabled?: boolean;
  backLabel?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onBack}
        disabled={disabled}
        aria-label={backLabel}
        className="text-muted-foreground hover:text-foreground grid size-9 shrink-0 place-items-center rounded-full transition-colors active:scale-95 disabled:opacity-50"
      >
        <ArrowLeft className="size-5" />
      </button>
      <h2 className="font-display flex-1 text-base font-semibold">{title}</h2>
      {step && (
        <span className="text-muted-foreground text-xs font-medium tabular-nums">{step}</span>
      )}
    </div>
  );
}
